import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getCourses,
  getClass,
  getClasses,
  createClass,
  createTimetable,
  updateClass,
  updateTeacher,
  deleteClass,
  updateClassTime,
  updateClassInfo,
  getClass2,
  getCoursesInSemester,
} from '../api/campusClasses.api';
import {
  ICourse,
  IClass,
  IGetClass,
  IGetCourses,
  IClassList,
  IGetClasses,
  IClassReq,
  ITeacherReq,
  IClassTimeReq,
} from '../models/campusClasses.model';

interface IClassState {
  courses?: ICourse[];
  chosenClass?: IClass | null;
  classes?: IClassList[];
  isLoading: boolean;
  error?: AxiosError | string;
  isDirectCounsel?: string | boolean;
}

const initialState: IClassState = {
  courses: [],
  chosenClass: null,
  classes: [],
  isLoading: false,
  isDirectCounsel: false,
};

const classSlicer = createSlice({
  name: 'class',
  initialState,
  reducers: {
    getCoursesStart: (state: IClassState) => {
      state.courses = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getCoursesSuccess: (
      state: IClassState,
      action: PayloadAction<IGetCourses>,
    ) => {
      state.courses = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCoursesFailure: (
      state: IClassState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.courses = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getClassStart: (state: IClassState) => {
      state.chosenClass = null;
      state.isLoading = true;
      state.error = undefined;
    },
    getClassSuccess: (state: IClassState, action: PayloadAction<IGetClass>) => {
      state.chosenClass = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getClassFailure: (
      state: IClassState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.chosenClass = null;
      state.isLoading = false;
      state.error = action.payload;
    },
    getClassesStart: (state: IClassState) => {
      state.classes = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getClassesSuccess: (
      state: IClassState,
      action: PayloadAction<IGetClasses>,
    ) => {
      state.classes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getClassesFailure: (
      state: IClassState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.classes = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IClassState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: IClassState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IClassState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IClassState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    deleteSuccess: (state: IClassState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    //20240507 aiden 대시보드 > 학생 클릭 > 학생 관리 상담 탭 이동을 위한 reducer추가
    setStudentTabIndex: (state: IClassState, action) => {
      state.isDirectCounsel = action.payload;
    },
  },
});

export const {
  getCoursesStart,
  getCoursesSuccess,
  getCoursesFailure,
  getClassStart,
  getClassSuccess,
  getClassFailure,
  getClassesStart,
  getClassesSuccess,
  getClassesFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
  deleteSuccess,
  setStudentTabIndex,
} = classSlicer.actions;

export default classSlicer.reducer;

export const doGetCourses =
  (semesterCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCoursesStart());
      const response = await getCourses(semesterCode);
      dispatch(getCoursesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCoursesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetCoursesInSemester =
  (semesterCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getCoursesStart());
      const response = await getCoursesInSemester(semesterCode);
      dispatch(getCoursesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getCoursesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetClass =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getClassStart());
      const response = await getClass(id);
      dispatch(getClassSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getClassFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetClass2 =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getClassStart());
      const response = await getClass2(id);
      // @ts-ignore
      dispatch(getClassSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getClassFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetClasses =
  (campusCode: string, semesterCode: number): any =>
  async (dispatch: any) => {
    try {
      dispatch(getClassesStart());
      const response = await getClasses(campusCode, semesterCode);
      dispatch(getClassesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getClassesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateClass =
  (campusId: string, data: IClassReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createClass(campusId, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateTimetable =
  (classCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createTimetable(classCode);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateClass =
  (classCode: string, data: IClassReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateClass(classCode, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateClassInfo =
  (classCode: string, data: IClassReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateClassInfo(classCode, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateClassTime =
  (classTimeCode: number, data: IClassTimeReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateClassTime(classTimeCode, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateTeacher =
  (id: string, data: ITeacherReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateTeacher(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doDeleteClass =
  (classCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await deleteClass(classCode);
      dispatch(deleteSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
